/**
 * Estado e regras puras da tabela de envios: busca, filtros combinaveis com
 * contagem, ordenacao por coluna e "ver mais".
 *
 * Decisoes nao obvias:
 *  1. Contagem de cada chip e calculada com OS OUTROS filtros aplicados
 *     (faceta): "Pausada 3" quer dizer "3 pausadas nesta empresa, neste
 *     canal, com esta busca". Contar sobre a lista inteira faria o chip
 *     prometer linhas que nao aparecem.
 *  2. Empresa padrao = a empresa em foco do cabecalho; "todas" mostra a
 *     coluna de empresa. Com centenas de empresas o filtro e um Select com
 *     busca do navegador, nao chips.
 *  3. "Ver mais" em lotes de 10: nunca renderizar a lista inteira. Mudar
 *     filtro ou ordem volta ao primeiro lote.
 *  4. Ordenar por custo por recuperacao coloca "sem recuperacao" no fim nos
 *     dois sentidos: nulo nao e zero nem infinito.
 */

import { custoDoEnvio, porUnidade, razaoBps } from './comparar';
import type { Broadcast, BroadcastStatus, SenderKind } from './types';
import { normalizarBusca } from './vocabulario';

export const LOTE_DA_TABELA = 10;

export type ColunaEnvios = 'criado' | 'progresso' | 'recuperado' | 'custo_por_recuperacao' | 'optout';

export interface OrdemEnvios {
  readonly coluna: ColunaEnvios;
  readonly desc: boolean;
}

export interface EstadoTabelaEnvios {
  readonly busca: string;
  /** `todas` ou o id de uma empresa. */
  readonly empresa: string;
  readonly status: 'todos' | BroadcastStatus;
  readonly canal: 'todos' | SenderKind;
  readonly ordem: OrdemEnvios;
  readonly limite: number;
}

export function estadoInicialDaTabela(empresa: string): EstadoTabelaEnvios {
  return { busca: '', empresa, status: 'todos', canal: 'todos', ordem: { coluna: 'criado', desc: true }, limite: LOTE_DA_TABELA };
}

export function progressoBps(e: Broadcast): number {
  return razaoBps(e.sentCount, e.totalContacts) ?? 0;
}

export function optOutBps(e: Broadcast): number | null {
  return razaoBps(e.optOutCount, e.sentCount + e.optOutCount);
}

export function custoPorRecuperacao(e: Broadcast): number | null {
  return porUnidade(custoDoEnvio(e), e.paidCount);
}

type Filtro = Omit<EstadoTabelaEnvios, 'ordem' | 'limite'>;

function passa(e: Broadcast, f: Filtro, nomeEmpresa: (id: string) => string, ignorar: 'status' | 'canal' | null): boolean {
  if (f.empresa !== 'todas' && e.companyId !== f.empresa) {
    return false;
  }
  if (ignorar !== 'status' && f.status !== 'todos' && e.status !== f.status) {
    return false;
  }
  if (ignorar !== 'canal' && f.canal !== 'todos' && e.senderKind !== f.canal) {
    return false;
  }
  const termo = normalizarBusca(f.busca.trim());
  if (termo === '') {
    return true;
  }
  return normalizarBusca(`${e.name} ${e.templateName} ${nomeEmpresa(e.companyId)} ${e.id}`).includes(termo);
}

export function filtrarEnvios(envios: readonly Broadcast[], f: Filtro, nomeEmpresa: (id: string) => string): readonly Broadcast[] {
  return envios.filter((e) => passa(e, f, nomeEmpresa, null));
}

/** Contagem por status e por canal, cada uma com os outros filtros aplicados. */
export function contarFacetas(
  envios: readonly Broadcast[],
  f: Filtro,
  nomeEmpresa: (id: string) => string,
): { readonly status: Readonly<Record<'todos' | BroadcastStatus, number>>; readonly canal: Readonly<Record<'todos' | SenderKind, number>> } {
  const semStatus = envios.filter((e) => passa(e, f, nomeEmpresa, 'status'));
  const semCanal = envios.filter((e) => passa(e, f, nomeEmpresa, 'canal'));
  const porStatus = (s: BroadcastStatus): number => semStatus.filter((e) => e.status === s).length;
  const porCanal = (c: SenderKind): number => semCanal.filter((e) => e.senderKind === c).length;
  return {
    status: {
      todos: semStatus.length,
      em_andamento: porStatus('em_andamento'),
      agendada: porStatus('agendada'),
      pausada: porStatus('pausada'),
      concluida: porStatus('concluida'),
    },
    canal: { todos: semCanal.length, evolution: porCanal('evolution'), oficial: porCanal('oficial') },
  };
}

function valorDaColuna(e: Broadcast, coluna: ColunaEnvios): number | null {
  switch (coluna) {
    case 'criado':
      return new Date(e.createdAt).getTime();
    case 'progresso':
      return progressoBps(e);
    case 'recuperado':
      return e.recoveredAmountCents;
    case 'custo_por_recuperacao':
      return custoPorRecuperacao(e);
    case 'optout':
      return optOutBps(e);
  }
}

export function ordenarEnvios(envios: readonly Broadcast[], ordem: OrdemEnvios): readonly Broadcast[] {
  return [...envios].sort((a, b) => {
    const va = valorDaColuna(a, ordem.coluna);
    const vb = valorDaColuna(b, ordem.coluna);
    if (va === null && vb === null) {
      return 0;
    }
    if (va === null) {
      return 1;
    }
    if (vb === null) {
      return -1;
    }
    return ordem.desc ? vb - va : va - vb;
  });
}

/** Clicar na coluna ativa inverte o sentido; outra coluna comeca decrescente. */
export function alternarOrdem(atual: OrdemEnvios, coluna: ColunaEnvios): OrdemEnvios {
  return atual.coluna === coluna ? { coluna, desc: !atual.desc } : { coluna, desc: coluna !== 'custo_por_recuperacao' };
}
